import { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from '../image-component/image.component';
import ModalDialog from '../modal-dialog/modal-dialog.component';
import { SpinnerCircle } from '../spinner/spinner.styles';
import { ReactComponent as DeleteIcon } from '../../assets/delete-icon.svg';
import { ReactComponent as AttachmentImageIcon } from './attachment-image.svg';
import { ReactComponent as CheckIcon } from './check.svg';
import goodPhoto from './parcel-items-photo-good.png';
import { ReactComponent as SparkleIcon } from './sparkle.svg';
import { ModalParcelItemsPhotoSelectionContainer } from './styles';
import { ReactComponent as UploadFileIcon } from './upload-file.svg';
import { ReactComponent as WrongIcon } from './wrong.svg';
import wrongPhoto from './parcel-items-photo-wrong.png';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import { normalizePhotoFile } from './image-file.helper';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/heic',
  'image/jpeg',
  'image/png',
];
const ACCEPTED_FILE_EXTENSIONS = ['.heic', '.jpeg', '.jpg', '.pdf', '.png'];

const formatFileSize = (size: number) => {
  const sizeInMegabytes = size / (1024 * 1024);
  const formattedSize = Number(sizeInMegabytes.toFixed(1));

  return `${formattedSize} MB`;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (file: File) => void | Promise<void>;
}

const ModalParcelItemsPhotoSelection = ({
  isOpen,
  onClose,
  onSelect,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFilePickerOpening, setIsFilePickerOpening] = useState(false);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const filePickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filePickerFocusHandlerRef = useRef<(() => void) | null>(null);

  const clearFilePickerFocusHandler = useCallback(() => {
    if (!filePickerFocusHandlerRef.current) return;

    window.removeEventListener('focus', filePickerFocusHandlerRef.current);
    filePickerFocusHandlerRef.current = null;
  }, []);

  const finishOpeningFilePicker = useCallback(() => {
    clearFilePickerFocusHandler();

    if (filePickerTimerRef.current) {
      clearTimeout(filePickerTimerRef.current);
    }

    filePickerTimerRef.current = setTimeout(() => {
      setIsFilePickerOpening(false);
      filePickerTimerRef.current = null;
    }, 150);
  }, [clearFilePickerFocusHandler]);

  useEffect(() => {
    const fileInput = inputRef.current;
    const handleFilePickerCancel = () => finishOpeningFilePicker();

    fileInput?.addEventListener('cancel', handleFilePickerCancel);

    return () => {
      fileInput?.removeEventListener('cancel', handleFilePickerCancel);
      if (filePickerTimerRef.current) {
        clearTimeout(filePickerTimerRef.current);
      }
      clearFilePickerFocusHandler();
    };
  }, [clearFilePickerFocusHandler, finishOpeningFilePicker]);

  const selectFile = async (file?: File) => {
    if (!file) return;

    const normalizedName = file.name.toLowerCase();
    const hasAcceptedExtension = ACCEPTED_FILE_EXTENSIONS.some((extension) =>
      normalizedName.endsWith(extension)
    );

    if (!ACCEPTED_FILE_TYPES.includes(file.type) && !hasAcceptedExtension) {
      setSelectedFile(null);
      setFileError(t('parcel_items_ai_photo_modal_invalid_type'));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setSelectedFile(null);
      setFileError(t('parcel_items_ai_photo_modal_file_too_large'));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setFileError(null);
    setIsFileProcessing(true);

    try {
      const normalizedFile = await normalizePhotoFile(file);

      if (normalizedFile.size > MAX_FILE_SIZE) {
        setSelectedFile(null);
        setFileError(t('parcel_items_ai_photo_modal_file_too_large'));
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      setSelectedFile(normalizedFile);
    } catch {
      setSelectedFile(null);
      setFileError(t('parcel_items_ai_photo_modal_heic_conversion_failed'));
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setIsFileProcessing(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setIsFilePickerOpening(false);
    setIsFileProcessing(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openFilePicker = () => {
    if (!isMobile) {
      setIsFilePickerOpening(true);

      clearFilePickerFocusHandler();
      filePickerFocusHandlerRef.current = finishOpeningFilePicker;
      window.addEventListener('focus', finishOpeningFilePicker, { once: true });
    }
    inputRef.current?.click();
  };

  const handleFileInputChange = (file?: File) => {
    selectFile(file);
    finishOpeningFilePicker();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files[0]);
  };

  const handleApprove = () => {
    if (!selectedFile) return;

    onClose();
    onSelect(selectedFile);
  };

  const steps = [
    t('parcel_items_ai_photo_modal_step_1'),
    t('parcel_items_ai_photo_modal_step_2'),
    t('parcel_items_ai_photo_modal_step_3'),
  ];

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      onApprove={handleApprove}
      approveBtnContent={t('parcel_items_ai_scan')}
      approveBtnDisabled={
        !selectedFile || isFilePickerOpening || isFileProcessing
      }
      cancelBtnContent={t('cancel')}
      width='min(80rem, calc(100vw - 1.6rem))'
      padding='3.2rem 4rem'
      paddingMobile='3.2rem 2.4rem 3rem'>
      <ModalParcelItemsPhotoSelectionContainer>
        <header className='photo-modal-header'>
          <SparkleIcon aria-hidden='true' />
          <h2>{t('parcel_items_ai_photo_modal_title')}</h2>
        </header>

        <div className='photo-guidelines'>
          <figure>
            <figcaption>
              <span className='guidance-icon'>
                <CheckIcon aria-hidden='true' />
              </span>
              {t('parcel_items_ai_photo_modal_good')}
            </figcaption>
            <div className='guidance-photo'>
              <Image
                src={goodPhoto}
                alt={t('parcel_items_ai_photo_modal_good_alt')}
                width='100%'
                height='100%'
                borderRadius='1.2rem'
              />
            </div>
          </figure>
          <figure>
            <figcaption>
              <span className='guidance-icon'>
                <WrongIcon aria-hidden='true' />
              </span>
              {t('parcel_items_ai_photo_modal_wrong')}
            </figcaption>
            <div className='guidance-photo'>
              <Image
                src={wrongPhoto}
                alt={t('parcel_items_ai_photo_modal_wrong_alt')}
                width='100%'
                height='100%'
                borderRadius='1.2rem'
              />
            </div>
          </figure>
        </div>

        <ol className='photo-steps'>
          {steps.map((step, index) => (
            <li key={step}>
              <span aria-hidden='true'>{index + 1}</span>
              <p>{step}</p>
            </li>
          ))}
        </ol>

        <section className='file-selection'>
          <h3>{t('parcel_items_ai_photo_modal_choose_file')}</h3>
          <input
            ref={inputRef}
            className='file-input'
            type='file'
            accept='.pdf,.png,.jpg,.jpeg,.heic,application/pdf,image/png,image/jpeg,image/heic'
            onChange={(event) => handleFileInputChange(event.target.files?.[0])}
          />
          {!selectedFile && (
            <>
              <div
                className={`dropzone${isDragging ? ' is-dragging' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}>
                {isFilePickerOpening || isFileProcessing ? (
                  <span className='upload-file-spinner'>
                    <SpinnerCircle aria-hidden='true' />
                  </span>
                ) : (
                  <button
                    type='button'
                    className='upload-file-button'
                    onClick={openFilePicker}>
                    <span className='upload-file-icon'>
                      <UploadFileIcon aria-hidden='true' />
                    </span>
                    <span className='desktop-drop-copy'>
                      {t('parcel_items_ai_photo_modal_drag_drop')}
                    </span>
                    <span className='upload-file-label'>
                      {t('parcel_items_ai_photo_modal_upload_file')}
                    </span>
                  </button>
                )}
              </div>
              <p
                className={`file-hint${fileError ? ' is-error' : ''}`}
                aria-live='polite'>
                {fileError || t('parcel_items_ai_photo_modal_file_hint')}
              </p>
            </>
          )}
          {selectedFile && (
            <div className='attachment-list'>
              <div className='attachment-item'>
                <span className='attachment-type-icon'>
                  <span>
                    <AttachmentImageIcon aria-hidden='true' />
                  </span>
                </span>
                <span className='attachment-details'>
                  <span className='attachment-name'>{selectedFile.name}</span>
                  <span className='attachment-size'>
                    {formatFileSize(selectedFile.size)}
                  </span>
                </span>
                <button
                  type='button'
                  className='remove-attachment-button'
                  aria-label={t(
                    'parcel_items_ai_photo_modal_remove_attachment'
                  )}
                  onClick={removeSelectedFile}>
                  <span>
                    <DeleteIcon aria-hidden='true' />
                  </span>
                </button>
              </div>
            </div>
          )}
        </section>
      </ModalParcelItemsPhotoSelectionContainer>
    </ModalDialog>
  );
};

export default ModalParcelItemsPhotoSelection;
